import Immutable from 'immutable';
import { createSelector } from 'reselect';
import {sortArray, filterProducts, filteredOutQuotes, pageContext} from 'ctm-util-functions';

import { selectedProductIdSelector } from './base';

/**
 * Fetch the current result set list of products.
 * @param {Immutable.List} payloadResults The results payload on state
 * @param {Immutable.Map} resultsConfig The results.config point on state
 * @returns {Immutable.List} An Immutable List of products in the current result set.
 */
export const getDescribedResultsSet = (payloadResults, resultsConfig = Immutable.Map()) => {
    let resultSet = payloadResults;
    if (resultsConfig.get('quoteDescriptor')) {
        resultSet = payloadResults.filter((describedResults) => {
            return describedResults.get('descriptor') === resultsConfig.get('quoteDescriptor');
        });
    }
    return Immutable.List.isList(resultSet) && Immutable.List.isList(resultSet.getIn([0, 'quotes'])) ? resultSet.getIn([0, 'quotes']) : Immutable.List();
};

/**
 * Filters the list to find the results described, or returns the first one.
 * @param {Immutable.List} payloadResults     - The payload results ('results' -> 'payload' -> 'results').
 * @param {Immutable.Map} resultsConfig      - resultsConfig from the state.
 * @returns {Immutable.List}                    - Immutable List of quotes.
 */
const filterAndSortResults = (payloadResults, resultsConfig) => {
    let resultQuotes = getDescribedResultsSet(payloadResults, resultsConfig);

    const filteringConfig = resultsConfig.get('filters');
    const sortingConfig = resultsConfig.get('sorting');

    if (resultQuotes && Immutable.Map.isMap(filteringConfig)) {
        const filtered = filterProducts(filteringConfig, resultQuotes);
        resultQuotes = Immutable.List(filtered);
    }

    if (resultQuotes && Immutable.Map.isMap(sortingConfig)) {
        // resultQuotes is a List. Lists are ordered indexed dense collections, much like an Array.
        const sorted = sortArray(sortingConfig, resultQuotes);
        resultQuotes = Immutable.List(sorted);
    }
    return resultQuotes;
};

const filteredOutResults = (payloadResults, resultsConfig) => {
    const resultQuotes = getDescribedResultsSet(payloadResults, resultsConfig);
    const filteringConfig = resultsConfig.get('filters');

    if (resultQuotes && Immutable.Map.isMap(filteringConfig)) {
        return Immutable.List(filteredOutQuotes(filteringConfig, resultQuotes));
    }

    return Immutable.List();
};

/**
 * Returns a de-duped mapping of brand codes, labels, and if they quoted.
 * NOTE: The assumption is that we will want to sort alphabetically in all cases.
 * If this needs to be toggleable, we may need to move the sorting logic to another method.
 * @param {List} describedResults The list of quotes.
 * @param {Boolean} onlyAvailableProducts - Set to true to only include the available product for a brand,
 * eg if there are 2 products for a brand, but one is unavailable, skip this and add the available product so that available brand counts are correct
 * @returns {List<Map>} A List of Maps: brand codes, labels and if the provider is available.
 */
const getDedupedQuotedBrands = (describedResults, onlyAvailableProducts = false) => {
    const codes = [];
    return describedResults
        .map((result) => {
            return Immutable.Map({
                providerMeta: Immutable.Map({
                    code: result.getIn(['providerMeta', 'code']),
                    name: result.getIn(['providerMeta', 'name'])
                }),
                productMeta: Immutable.Map({
                    id: result.getIn(['productMeta', 'id']),
                    name: result.getIn(['productMeta', 'name']) || result.getIn(['providerMeta', 'name']),
                    available: result.getIn(['productMeta', 'available'])
                })
            });
        })
        .reduce((accumulator, brand) => {
            const code = brand.getIn(['providerMeta', 'code']);
            // don't add brands of unavailable products
            const available = brand.getIn(['productMeta', 'available']);
            const availableProductCheck = onlyAvailableProducts ? available : true;
            if (!codes.includes(code) && availableProductCheck) {
                codes.push(code);
                // eslint-disable-next-line no-param-reassign
                accumulator = accumulator.push(brand);
            }
            return accumulator;
        }, Immutable.List())
        .sort((current, next) => {
            const currentLabel = current.getIn(['providerMeta', 'name']);
            const nextLabel = next.getIn(['providerMeta', 'name']);
            if (currentLabel < nextLabel) {
                return -1;
            }

            if (currentLabel > nextLabel) {
                return 1;
            }
            return 0;
        });
};

/**
 * Selector to get the SortResultsConfig state.
 * @param  {Immutable.Map} state    - The application Redux store.
 * @returns {Immutable.Map}         - The Sort Results Config state.
 */
export const resultsConfigSelector = (state) => {
    return quoteResultsSelector(state).get('config');
};

/**
 * Selector to get the productInfo state.
 * @param  {Immutable.Map} state The application Redux store.
 * @returns {Immutable.Map} The productInfo state.
 */
export const productInfoSelector = (state) => {
    return state.getIn(['vertical', 'quote', 'results', 'productInfo']);
};

/**
 * Selector to get the quote ID from state.
 * @param  {Immutable.Map} state The application Redux store.
 * @returns {String|Number} The quoteId state.
 */
export const quoteIdSelector = (state) => {
    const results = quoteResultsSelector(state);
    return results.getIn(['payload', 'resultsMeta', 'quoteId']);
};

/**
 * Selector to get the available count of results from state
 * @param  {Immutable.Map} state The application Redux store.
 * @param  {Number} excessFilterValue The Excess filter value.
 * @returns {Number} The number of available quote products.
 */
export const availableProductResultsSelector = (state, excessFilterValue) => {
    const results = quoteResultsSelector(state);
    const isCar = state.getIn(['page', 'vertical']) === 'car-insurance';
    const hideHighExcess = isCar && state.getIn(['page', 'isHideHighExcess']);
    if (hideHighExcess) {
        const quotes = sortedAndFilteredProductsSelector(state);
        return Immutable.List(quotes.filter((quote) => quote.get('productMeta').get('excess') <= excessFilterValue)).size;
    }
    return results.getIn(['payload', 'resultsMeta', 'availableCount']);
};

/**
 * Selector to get the available count of results from state
 * @param  {Immutable.Map} state The application Redux store.
 * @param  {Number} excessFilterValue The Excess filter value.
 * @returns {Boolean} if any results in the quote payload have products which have an excess that is higher
 *                    than the excess filter value.
 */
export const hasProductsWithExcessHigherThanFilterValue = (state, excessFilterValue) => {
    const quotes = sortedAndFilteredProductsSelector(state);
    return quotes.some((quote) => quote.get('productMeta').get('excess') > excessFilterValue);
};

/**
 * Selector to get the total count of results from state
 * @param  {Immutable.Map} state The application Redux store.
 * @returns {Number} The total count of results.
 */
export const totalProductResultsSelector = (state) => {
    const results = quoteResultsSelector(state);
    return results.getIn(['payload', 'resultsMeta', 'totalCount']);
};

/**
 * Selector to get the ResultsPayload state.
 * @param  {Immutable.Map} state The application Redux store.
 * @returns {Immutable.List} The Results Payload results state.
 */
export const payloadResultsSelector = (state) => {
    return state.getIn(['vertical', 'quote', 'results', 'payload', 'results']);
};

/**
 * Selector to get the quote Results state.
 * @param  {Object} state   - The application Redux store.
 * @returns {Immutable.Map} - The Results state tree.
 */
export const quoteResultsSelector = (state) => {
    return state.getIn(['vertical', 'quote', 'results']);
};

/**
 * Selector to get the code of omitted providers.
 * If experimentId is set to omitProviders, this selector will return a list of code of omitted providers
 * otherwise, an empty list will be returned.
 * @param  {Immutable.Map} state The application Redux store.
 * @returns {Immutable.List} a list of code of omitted providers
 */
export const omittedBrandsSelector = (state) => {
    const experimentId = state.getIn(['page', 'experimentId']);
    if (experimentId === 'omitProviders') {
        const results = quoteResultsSelector(state);
        return results.getIn(['payload', 'resultsMeta', 'omittedProviders']) || Immutable.List([]);
    }
    return Immutable.List([]);
};

/**
 *
 * @param {Immutable.Map} brand The product object.
 * @param {Boolean} available true if looking for available products, false if unavailable
 * @returns {Boolean} true or false if the brand is available
 * @private
 */
const _brandFilter = (brand, available) => Immutable.Map.isMap(brand) && brand.getIn(['productMeta', 'available']) === available;

/**
 * Returns a filtered List of the brands to the ones that has required availability.
 * @param {Immutable.Map} state The state object
 * @param {Boolean} available Whether it should return available = true or available = false products.
 * @returns {List<Map>} A List of of brand data Maps.
 * If available parameter is not provided or provided with 'true' value,
 * this selector returns a list of a available brands,
 * if available parameter input has 'false' value, this selector returns a list of unavailable brands.
 */
export const getAvailableReturnedBrands = (state, available = true) => {
    // @todo: the logic around getting only available brands, vs getting all brands really needs to be refactored
    // the getDedupedQuotedBrands function is being used for both use case, but is a long way down the chain of selectors
    const selector = available ? availableQuotedBrandsSelector : quotedBrandsSelector;

    const experimentId = state.getIn(['page', 'experimentId']);
    const omittedProviders = omittedBrandsSelector(state);

    const brands = selector(state).filter((brand) => {
        const brandCode = brand.getIn(['providerMeta', 'code']);
        // When finding unavailable brands, a brand is considered unavailable if it is included in
        // omittedProviderList, even if the brand is stored as available in the state tree.

        if (available === false && experimentId === 'omitProviders') {
            const providerOmitted = omittedProviders && omittedProviders.some((omittedProviderMeta) => omittedProviderMeta.get('code') === brandCode);
            if (providerOmitted) {
                return true;
            }
        }
        return _brandFilter(brand, available);
    });

    return brands;
};

/**
 * Get the Product IDS of available or unavailable brands
 * @param {Immutable.Map} state The state object
 * @param {Boolean} available Whether it should return available = true or available = false products.
 * @returns {Immutable.List} An Immutable List of the available or unavailable product ids
 */
export const getProductIdsByAvailability = (state, available = true) => {
    return getDescribedResultsSet(payloadResultsSelector(state), resultsConfigSelector(state))
        .filter((brand) => _brandFilter(brand, available))
        .map((brand) => brand.getIn(['productMeta', 'id']));
};

/**
 * Creates a reselect selector for the product from the selectedProductId.
 * @returns {Object}         - The selected product details.
 */
export const selectedProductSelector = createSelector(
    [selectedProductIdSelector, payloadResultsSelector],
    (selectedProductId, results) => {
        if (results === undefined) {
            return Immutable.Map();
        }

        // When showing Ref number in energy results
        if (!selectedProductId) {
            return results
                .get(0)
                .get('quotes')
                .get(0);
        }

        for (const providerResults of results) {
            const resultSet = providerResults.get('quotes');
            const product = resultSet.filter((result) => {
                return result.getIn(['productMeta', 'id']) === selectedProductId;
            });
            if (product.size === 1) {
                return product.get(0);
            }
        }

        if (window.Raven) {
            window.Raven.captureException("Couldn't find a matching product", {
                productId: selectedProductId
            });
        }

        return undefined;
    }
);

/**
 * Used to retrieve a List of all brands
 * @returns {List} A List of brand objects
 */
export const quotedBrandsSelector = createSelector(
    [resultsConfigSelector, payloadResultsSelector],
    (resultsConfig, payloadResults) => {
        if (payloadResults && payloadResults.getIn([0, 'quotes'])) {
            return getDedupedQuotedBrands(getDescribedResultsSet(payloadResults, resultsConfig));
        }
        return Immutable.List();
    }
);

/**
 * Used to retrieve a List of all available brands - it was easier to copy the quotedBrandsSelector and pass the available flag
 * to getDedupedQuotedBrands than to try and make a version of quotedBrandsSelector that could take available as an arg
 * without this, getDedupedQuotedBrands can return an unavailable product as it has 'deduped' the available product for brand
 * that has available and unavailable products
 * @returns {List} A List of brand objects
 */
export const availableQuotedBrandsSelector = createSelector(
    [resultsConfigSelector, payloadResultsSelector],
    (resultsConfig, payloadResults) => {
        if (payloadResults && payloadResults.getIn([0, 'quotes'])) {
            return getDedupedQuotedBrands(getDescribedResultsSet(payloadResults, resultsConfig), true);
        }
        return Immutable.List();
    }
);

/**
 * Used to retrieve the list of results that displays on the page.
 * It applies filters and sorting rules through filterAndSortResults
 * @returns {List} An immutable list of products
 */
export const sortedAndFilteredProductsSelector = createSelector(
    [resultsConfigSelector, payloadResultsSelector],
    (resultsConfig, payloadResults) => {
        if (payloadResults && payloadResults.getIn([0, 'quotes'])) {
            return filterAndSortResults(payloadResults, resultsConfig);
        }
        return Immutable.List();
    }
);

export const filteredOutProductsSelector = createSelector(
    [resultsConfigSelector, payloadResultsSelector],
    (resultsConfig, payloadResults) => {
        if (payloadResults && payloadResults.getIn([0, 'quotes'])) {
            return filteredOutResults(payloadResults, resultsConfig);
        }
        return Immutable.List();
    }
);

/**
 * Used to retrieve the list of results from the payload
 * @returns {List} An immutable list of products
 */
export const describedProductsSelector = createSelector(
    [resultsConfigSelector, payloadResultsSelector],
    (resultsConfig, payloadResults) => {
        if (payloadResults && payloadResults.getIn([0, 'quotes'])) {
            return getDescribedResultsSet(payloadResults, resultsConfig);
        }
        return Immutable.List();
    }
);

/**
 * Return a count of how many products in payloadResults match the supplied features code.
 * @param {Immutable.List} payloadResults - The List of result sets from payloadResultsSelector
 * @param {String} code - The code to count
 * @returns {Number} - The number of products that match the code
 */
export const countFeatures = (payloadResults, code) => {
    let count = 0;
    if (payloadResults) {
        for (const resultSet of payloadResults) {
            for (const quote of resultSet.get('quotes')) {
                const hasCode = quote.getIn(['productMeta', ...code.split('.')]) === 'Y';
                if (hasCode) {
                    count += 1;
                }
            }
        }
    }
    return count;
};

/**
 * Simple props selector that returns all the feature codes for a CheckboxListFilter. Codes are taken from filterProps.options
 * @param  {Immutable.Map} state - The application Redux state.
 * @param {Object} props - Component props from a CheckboxListFilter
 * @returns {Array} - The array of feature codes
 */
export const getFeatureCodes = (state, props) => {
    const { options = [] } = props.filterProps;
    return options.map((item) => item.code);
};

/**
 * Return the number of products that match each feature code, mapped to the feature codes
 * @returns {Object} - each feature code mapped to the number of products that support it
 */
export const featureCountSelector = createSelector(
    [payloadResultsSelector, getFeatureCodes],
    (payloadResults, featureCodes) => {
        return featureCodes.reduce((allCounts, code) => {
            // eslint-disable-next-line no-param-reassign
            allCounts[code] = countFeatures(payloadResults, code);
            return allCounts;
        }, {});
    }
);

/**
 * Return a count of how many products in payloadResults match the supplied brand code.
 * @param {Immutable.List} payloadResults - The List of result sets from payloadResultsSelector
 * @param {String} brand - The brand code to count
 * @returns {Number} - The number of products that match the brand
 */
export const countBrands = (payloadResults, brand) => {
    let count = 0;
    for (const resultSet of payloadResults) {
        for (const quote of resultSet.get('quotes')) {
            const code = quote.getIn(['providerMeta', 'code']);
            const isAvailable = quote.getIn(['productMeta', 'available']);
            const isAvailableBrand = code === brand && isAvailable;
            if (isAvailableBrand) {
                count += 1;
            }
        }
    }
    return count;
};

/**
 * Return the number of products that match each brand code, mapped to the brand codes
 * @returns {Object} - each brand code mapped to the number of products that support it
 */
export const brandCountSelector = createSelector(
    [payloadResultsSelector, getAvailableReturnedBrands],
    (payloadResults, brands) => {
        return brands.reduce((allCounts, brand) => {
            const code = brand.getIn(['providerMeta', 'code']);
            // eslint-disable-next-line no-param-reassign
            allCounts[code] = countBrands(payloadResults, code);
            return allCounts;
        }, {});
    }
);

/**
 * Selector to get the quote brand codes
 * @param  {Immutable.Map} quotes    - The results quotes
 * @returns {Immutable.List} - The brand codes from results
 */
export const getBrandCodes = (quotes) => {
    let brands = Immutable.List();
    quotes.forEach((quote) => {
        if (quote.getIn(['productMeta', 'available'])) {
            const code = quote.getIn(['providerMeta', 'code']);
            if (!brands.includes(code)) {
                brands = brands.push(code);
            }
        }
    });
    return brands;
};

/**
 * Retrieves the value of the HasSavedResults from the results config in state.
 *
 * @return {boolean} The value of the HasSavedResults from the results config, or undefined if it does not exist.
 */
export const getHasSavedResults = () => {
    const { store } = pageContext.getProps();
    const config = JSON.parse(JSON.stringify(resultsConfigSelector(store.getState())));
    return !!config.helpers ? config.helpers.userHasSavedResults : false;
};
